<script lang="ts" setup>
import { nextTick, onMounted, reactive, ref, watch, onBeforeUnmount } from 'vue';
import { PCheckbox, PInput } from '@profeat/ui-kit';
import { onClickOutside, onKeyStroke, watchDebounced } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';

import { customerConstants } from '~/components/customers/helper';
import { SystemField } from '~/entities/contacts';
import { EMPTY_DATE, FilterBuilder } from '~/components/customers/filter-builder';

const { FILTER_DATE_DISPLAY_FORMAT, FILTER_DATE_FORMAT, DEFAULT_FILTER_DEBOUNCE } = customerConstants;
const { t } = useI18n();

const props = defineProps<{
  field: SystemField;
  filterBuilder: FilterBuilder;
  refWrap: HTMLElement;
  filterValue?: {
    chips: string;
    yearValue: string;
    dateCheckbox: boolean;
    dateLeft: string;
    dateRight: string;
  };
}>();
const emit = defineEmits<{
  (event: 'applyFilter', visible?: boolean, withoutCall?: boolean): void;
  (event: 'resetFilter', withoutCall?: boolean): void;
  (event: 'clickOutside'): void;
  (event: 'update:changed', val: boolean): void;
  (
    event: 'setFilterValue',
    payload: {
      chips: string;
      yearValue: string;
      dateCheckbox: boolean;
      dateLeft: string;
      dateRight: string;
    },
  ): void;
}>();

const now = dayjs();

const filterRef = ref(null);
const currentChip = ref('lte');
const yearInput = ref('');
const leftValue = ref('');
const rightValue = ref('');
const calendarValue = reactive<string[]>([]);
const chipsValueStorage = ref({ chips: 'lte', value: '' });
const yearInputRef = ref('');
const dateCheckbox = ref(false);

type DateRange = { from: string | null; to: string | null };

const dropdownCalendar = reactive({
  left: false,
  right: false,
});

const dataCalendar = reactive<DateRange>({
  from: null,
  to: null,
});

const isUnmounted = ref(false);

onBeforeUnmount(() => {
  isUnmounted.value = true;
});

const emitFilter = () => {
  emit('applyFilter');
  emit('setFilterValue', {
    chips: currentChip.value,
    dateCheckbox: dateCheckbox.value,
    yearValue: yearInput.value,
    dateLeft: leftValue.value,
    dateRight: rightValue.value,
  });
};
const setToFilter = (isOutside?: boolean) => {
  if (isUnmounted.value) return;

  if (props.filterValue?.dateCheckbox !== dateCheckbox.value) {
    props.filterBuilder.remove(props.field.id);

    if (dateCheckbox.value) {
      props.filterBuilder.find(props.field.id, EMPTY_DATE);
      emitFilter();
      return;
    }

    if (!yearInput.value && !leftValue.value && !rightValue.value) {
      emitFilter();
      return;
    }
  }

  if (currentChip.value === 'between') {
    if ((dropdownCalendar.left || dropdownCalendar.right) && !isOutside) return;
    if (
      !dayjs(leftValue.value, FILTER_DATE_DISPLAY_FORMAT).isValid() ||
      !dayjs(rightValue.value, FILTER_DATE_DISPLAY_FORMAT).isValid()
    ) {
      emit('resetFilter');
      return;
    }

    props.filterBuilder.between(
      props.field.id,
      dayjs(leftValue.value, FILTER_DATE_DISPLAY_FORMAT).format(),
      dayjs(rightValue.value, FILTER_DATE_DISPLAY_FORMAT)
        .add(23, 'hour')
        .add(59, 'minute')
        .add(59, 'second')
        .format(),
    );
  } else {
    const hasFilter = !!props.filterBuilder.getFiltersById(props.field.id).length;

    if (hasFilter && props.filterValue?.yearValue && !yearInput.value && currentChip.value !== props.filterValue.chips) {
      return;
    }

    if (hasFilter && yearInput.value && currentChip.value !== props.filterValue?.chips) {
      props.filterBuilder.remove(props.field.id);
    }

    if (hasFilter && !yearInput.value) {
      const isEmptyDate = props.filterBuilder.getFiltersById(props.field.id).find(({ value }) => value === EMPTY_DATE);

      if (!isEmptyDate) {
        emit('resetFilter');
        return;
      }
    }

    if (!yearInput.value) return;

    const date = dayjs().subtract(+yearInput.value, 'year');
    if (currentChip.value === 'eq') {
      const startOfYear = date.startOf('year').toISOString();
      const endOfYear = date.endOf('year').toISOString();
      props.filterBuilder.between(props.field.id, startOfYear, endOfYear);
    } else {
      // @ts-ignore
      props.filterBuilder[currentChip.value](props.field.id, date.toISOString());
    }
  }

  emitFilter();
};

const getChips = () => {
  return [
    { text: t('customers.filter.types.lte'), value: 'lte' },
    { text: t('customers.filter.types.eq'), value: 'eq' },
    { text: t('customers.filter.types.gte'), value: 'gte' },
    { text: t('customers.filter.types.between'), value: 'between' },
  ];
};
// const selectDateLeft = (value: string) => {
//   leftValue.value = dayjs(value).format(FILTER_DATE_DISPLAY_FORMAT);
//   dropdownCalendar.left = false;
// };
// const selectDateRight = (value: string) => {
//   rightValue.value = dayjs(value).format(FILTER_DATE_DISPLAY_FORMAT);
//   dropdownCalendar.right = false;
// };

onKeyStroke(['Enter'], () => {
  setToFilter();
});

onClickOutside(
  () => props.refWrap,
  () => {
    setToFilter(true);
    emit('clickOutside');
  }
);

watchDebounced(
  calendarValue,
  () => {
    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 15 },
);

watch(yearInput, (value) => {
  const changed = !!value || dateCheckbox.value;

  emit('update:changed', changed);
});
watch(dateCheckbox, (checkbox) => {
  if (!checkbox) props.filterBuilder.remove(props.field.id);
});

watchDebounced(
  yearInput,
  () => {
    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 5 },
);

watch([leftValue, rightValue], ([left, right]) => {
  const leftFormatted = dayjs(left, FILTER_DATE_DISPLAY_FORMAT);
  const rightFormatted = dayjs(right, FILTER_DATE_DISPLAY_FORMAT);
  if (leftFormatted.isValid()) {
    calendarValue.splice(0, 1, leftFormatted.format());
    dataCalendar.from = dayjs(left, FILTER_DATE_DISPLAY_FORMAT).format(FILTER_DATE_FORMAT);
  }
  if (rightFormatted.isValid()) {
    calendarValue.splice(1, 1, rightFormatted.format());
    dataCalendar.from = dayjs(left, FILTER_DATE_DISPLAY_FORMAT).format(FILTER_DATE_FORMAT);
  }
  const changed = left !== props.filterValue?.dateLeft || right !== props.filterValue?.dateRight;
  emit('update:changed', changed);
});
watchDebounced(
  dateCheckbox,
  () => {
    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 5 },
);
watch(currentChip, (newValue) => {
  nextTick(() => {
    if (yearInputRef.value) (yearInputRef.value as any).input.focus();
  });

  if (chipsValueStorage.value.chips === newValue) {
    yearInput.value = chipsValueStorage.value.value;
    return;
  }

  yearInput.value = '';

  emit('update:changed', false);
});
onMounted(() => {
  calendarValue.push(...[now.toISOString(), now.add(7, 'days').toISOString()]);

  const filter = props.filterValue;
  if (filter) {
    currentChip.value = filter.chips;
    yearInput.value = filter.yearValue;
    chipsValueStorage.value.chips = filter.chips;
    chipsValueStorage.value.value = filter.yearValue;
    dateCheckbox.value = filter.dateCheckbox;
    leftValue.value = filter.dateLeft;
    rightValue.value = filter.dateRight;
  }

  emit('setFilterValue', {
    chips: currentChip.value,
    dateCheckbox: dateCheckbox.value,
    yearValue: yearInput.value,
    dateLeft: leftValue.value,
    dateRight: rightValue.value,
  });
});
</script>

<template>
  <div class="birthday-filter" ref="filterRef">
    <PChips class="chips" v-model="currentChip" :chips="getChips()" />

    <div v-if="currentChip === 'between'" class="calendar-field">
      <div class="fields">
        <PInput
          class="input"
          v-model="leftValue"
          type="text"
          :mask="`##.##.####`"
          placeholder="ДД.MM.ГГГГ"
          autofocus
          :disabled="dateCheckbox"
        ></PInput>

        <hr class="divider" />

        <PInput class="input" v-model="rightValue" type="text" :mask="`##.##.####`" placeholder="ДД.MM.ГГГГ" :disabled="dateCheckbox"> </PInput>
      </div>
    </div>

    <div v-else class="years-input">
      <PInput
        v-model.number="yearInput"
        type="number"
        mask="###"
        inputMode="numeric"
        placeholder="0"
        autofocus
        ref="yearInputRef"
        :disabled="dateCheckbox"
      />
      <span>{{ $t('customers.filter.years') }}</span>
    </div>

    <div class="checkbox">
      <PCheckbox v-model="dateCheckbox" label="Нет даты" text-size="sm" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.birthday-filter {
  display: flex;
  flex-direction: column;
  grid-gap: 16px;

  :deep(.chips) {
    flex-wrap: nowrap;
    display: grid;
    grid-template-columns: repeat(4, 1fr);

    .chip {
      width: 100%;
      max-width: 100%;
      justify-content: center;
    }
  }

  .calendar-field {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    grid-gap: 16px;

    .fields {
      display: grid;
      grid-template-columns: 1fr 16px 1fr;
      align-items: center;
      gap: 8px;

      :deep(.dropdown) {
        width: 328px;
      }

      .divider {
        box-sizing: border-box;
        width: 16px;
        height: 1px;
        background: var(--common-black, #2b3238);
        outline: none;
        border: none;
      }
    }
  }

  .years-input {
    display: flex;
    align-items: center;
    gap: 10px;

    :deep(.p-field) {
      width: 100px;
    }

    .input {
      width: 70px;
    }

    span {
      color: var(--common-black, #2b3238);
      font-size: 14px;
      font-weight: 400;
      line-height: 143%;
    }
  }
}
</style>
